/**
 * DOCUMENTATION:
 * Component: CleanCodeSolid
 * Purpose: Teaching the five SOLID principles in a modern Web/JavaScript context.
 * * * Note for Junior Developers:
 * - S: Single Responsibility — A class/function should have one job.
 * - O: Open/Closed — You should be able to add new behavior without changing existing code.
 * - L: Liskov Substitution — You should be able to swap a parent for a child without breaking the app.
 * - I: Interface Segregation — Don't force code to depend on methods it doesn't use.
 * - D: Dependency Inversion — Depend on abstractions (interfaces), not concrete implementations.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "0.5fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
    },
}));

/* ✅ Main Component */
export default function CleanCodeSolid() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const solidPrinciples = useMemo(() => [
        { letter: "S", name: "Single Responsibility", desc: "A component or function should do one thing perfectly. If a component fetches data, filters it, and renders it, split it up." },
        { letter: "O", name: "Open / Closed", desc: "Software entities should be open for extension but closed for modification. Use props and composition to add features instead of editing the core file." },
        { letter: "L", name: "Liskov Substitution", desc: "Objects of a superclass should be replaceable with objects of a subclass without affecting the correctness of the program." },
        { letter: "I", name: "Interface Segregation", desc: "Clients shouldn't be forced to depend on methods they don't use. Don't pass a giant 'user' object to a component that only needs 'user.avatar'." },
        { letter: "D", name: "Dependency Inversion", desc: "High-level modules should not depend on low-level modules. Both should depend on abstractions (like Hooks or Context Providers)." },
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. Open / Closed Principle (The Refactor)",
            code: `// ❌ BAD: Every time we add a new role, we have to modify this 'switch'\nfunction getPermissions(user) {\n  switch(user.role) {\n    case 'admin': return ['read', 'write', 'delete'];\n    case 'editor': return ['read', 'write'];\n    default: return ['read'];\n  }\n}\n\n// ✅ GOOD: Logic is moved to data. The function is 'Closed' to modification.\nconst rolePermissions = {\n  admin:  ['read', 'write', 'delete'],\n  editor: ['read', 'write'],\n  guest:  ['read']\n};\n\nfunction getPermissions(user) {\n  return rolePermissions[user.role] || rolePermissions.guest;\n}`,
            desc: "By moving logic into a configuration object, you can add new roles without ever touching the logic of the 'getPermissions' function again. The code is now 'Open' to extension but 'Closed' to modification."
        },
        {
            title: "2. Interface Segregation (React Props)",
            code: `// ❌ BAD: This component only needs the name, but we passed the whole user object.\n// If the 'user' object structure changes, this component might break.\nfunction UserBadge({ user }) {\n  return <span>{user.name}</span>;\n}\n\n// ✅ GOOD: Component only depends on the data it actually uses.\nfunction UserBadge({ name }) {\n  return <span>{name}</span>;\n}`,
            desc: "Don't force components to know more than they need to. Passing only the specific primitives (strings, numbers) makes your components easier to reuse and harder to break."
        },
        {
            title: "3. Dependency Inversion (Abstraction)",
            code: `// ❌ BAD: The component is hard-coded to use 'LocalStorage'. \n// Hard to test and hard to change to a Database later.\nfunction Settings() {\n  const save = () => localStorage.setItem('theme', 'dark');\n  return <button onClick={save}>Save</button>;\n}\n\n// ✅ GOOD: The component depends on an abstraction (a custom hook).\n// We can swap LocalStorage for an API call inside the hook without touching the UI.\nfunction Settings() {\n  const { saveTheme } = useThemeSettings();\n  return <button onClick={() => saveTheme('dark')}>Save</button>;\n}`,
            desc: "High-level UI logic shouldn't care about low-level storage details. By using a Hook as an abstraction layer, you decouple your interface from your data sources."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    SOLID PRINCIPLES
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Building for Change
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            The primary goal of SOLID is to reduce <strong>Software Rot</strong>. Software Rot occurs when code becomes so rigid, fragile, and immobile that even a tiny change causes a cascade of bugs. These principles act as the architectural blueprint for professional, scalable software.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>The 5 Foundations</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {solidPrinciples.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: 900, color: "goldenrod", fontSize: "1.2rem" }}>{item.letter}</Box>
                                    <Box sx={{ fontWeight: "bold" }}>{item.name}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem" }}>{item.desc}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>


                    </SectionCard>
                </Grid>

                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/clean-code-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">BACK TO DASHBOARD</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    textTransform: "uppercase",
    "&:hover": { backgroundColor: "#e0b200" },
}));
